import { StyleSheet, View } from 'react-native'
import { Text, TouchableRipple } from 'react-native-paper'
import {
  IconBumpDown,
  IconBumpUp,
  IconBumpScores,
} from '../../../../utils/meldd-icons'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  BumpContributionInput,
  ContributionBump,
  ContributionBumpDetailFragmentDoc,
  useBumpContributionMutation,
  useBumpsForContributionQuery,
} from '../../../../generated/graphql'
import _orderBy from 'lodash/orderBy'
import { useAuthContext } from '../../../../contexts/auth-context'
import { MessageType } from '../../../../utils/message-type'
import Toast from 'react-native-root-toast'
import { useTranslation } from 'react-i18next'
import { BumpDetailsModal, BumpDetailsRef } from './bump-detail.modal'
import {
  BumpJustificationModal,
  CANCEL_BUMP,
  BumpJustificationRef,
} from './bum-justification.modal'
import { SocketEvents, useSocketRoom } from '../../../../socket/socket.hooks'
import { useNavigation } from '@react-navigation/native'
import { AppNavigation } from '../../../../navigation/types'
import { Row } from '../../../../@ui'
import * as Sentry from '@sentry/react-native'
import { useReplayApi } from '../../../../services/snapshots/replay-api.hook'
import { Contribution } from '../types'
import { useFeatures } from '../../../../contexts/feature.provider'

const countBumps = (bumps: ContributionBump[]) => {
  return bumps.reduce(
    (acc, b) => {
      if (b.value > 0) acc.positive += 1
      if (b.value < 0) acc.negative += 1
      return acc
    },
    { positive: 0, negative: 0 }
  )
}
interface BumpButtonsProps {
  contribution?: Contribution
  readonly?: boolean
}
const BumpScoreButton = ({
  score,
  onPress,
}: {
  score: number
  onPress: () => void
}) => {
  const styles = StyleSheet.create({
    scoreWrapper: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: '#888', // gray border
      borderRadius: 20,
      paddingHorizontal: 8,
      paddingVertical: 8,
    },
    scoreText: {
      marginLeft: 8,
      color: '#222',
    },
  })

  return (
    <TouchableRipple onPress={onPress}>
      <View style={styles.scoreWrapper}>
        <IconBumpScores size={32} mode="text" onPress={onPress} />
        <Text style={styles.scoreText}>{score}</Text>
      </View>
    </TouchableRipple>
  )
}
export function BumpButtons(props: BumpButtonsProps) {
  const { contribution, readonly } = props
  const { userId: loggedUserId } = useAuthContext()
  const bumpDetailsRef = useRef<BumpDetailsRef>(null)
  const bumpJustificationRef = useRef<BumpJustificationRef>(null)
  const { ownerBumps } = useFeatures()
  const { t } = useTranslation('bumps')
  const { authAction } = useAuthContext()
  const navigation = useNavigation<AppNavigation>()

  const [doBumpContribution, { loading: isBumping }] =
    useBumpContributionMutation({
      update: (cache, result) => {
        const { data } = result
        if (data) {
          // Add the bump to the list if id didn't exist
          const field = `contributionBumpsByContribution(${JSON.stringify({
            contributionId: contribution?.id,
          })})`
          cache.modify({
            id: 'ROOT_QUERY',
            fields: {
              [field]: (currentBumps: any[], { readField }) => {
                const newBumpRef = cache.writeFragment({
                  data: data.bumpContribution,
                  fragment: ContributionBumpDetailFragmentDoc,
                })
                const newBumpRefStr = JSON.stringify(newBumpRef)
                if (
                  currentBumps.some(
                    (ref) => JSON.stringify(ref) == newBumpRefStr
                  )
                ) {
                  return currentBumps
                }
                return [...currentBumps, newBumpRef]
              },
            },
          })
        }
      },
    })

  const { data, refetch } = useBumpsForContributionQuery({
    variables: { contributionId: contribution?.id || '' },
    skip: !contribution?.id,
  })
  useSocketRoom(contribution?.id || '').event(SocketEvents.Refresh, refetch)
  const bumps = useMemo(
    () =>
      _orderBy(
        data?.contributionBumpsByContribution || [],
        'updatedAt',
        'desc'
      ) as ContributionBump[],
    [data]
  )

  const isContributor = useMemo(
    () => contribution?.userId == loggedUserId,
    [contribution, loggedUserId]
  )
  const myBumpValue = useMemo(
    () => bumps.find((b) => b.userId == loggedUserId)?.value || 0,
    [bumps]
  )
  const counts = useMemo(() => countBumps(bumps), [bumps])
  const canBump = useMemo(
    () => !isBumping && (!isContributor || ownerBumps),
    [isContributor, ownerBumps, isBumping]
  )
  const saveBump = async (vote: string, reason: string) => {
    const addValue = vote == 'up' ? 1 : -1
    const value = myBumpValue + addValue
    if (Math.abs(value) <= 1) {
      const input: BumpContributionInput = {
        contributionId: contribution?.id || '',
        value: value,
        reason,
      }
      try {
        await doBumpContribution({ variables: { input } })
        Toast.show(t('Bump saved'), MessageType.info)
      } catch (e) {
        Toast.show((e as Error).message, MessageType.error)
        console.error(e)
        Sentry.captureException(e)
      }
    }
  }

  const handleClick = useCallback(
    (value: 'up' | 'down') => {
      const showReason = myBumpValue == 0
      // If is negative or positive, request the reason, otherwise is removing his bump
      showReason
        ? authAction(navigation, () =>
            bumpJustificationRef.current?.show(value)
          )
        : authAction(navigation, () => saveBump(value, ''))
    },
    [myBumpValue]
  )

  if (!contribution) {
    return null
  }

  return (
    <>
      <BumpDetailsModal ref={bumpDetailsRef} />
      <BumpJustificationModal
        ref={bumpJustificationRef}
        onResult={(value, reason) => {
          if (value === null) {
            return
          }
          if (reason === CANCEL_BUMP) {
            return
          }
          saveBump(value, reason)
        }}
      />
      <Row
        columnGap={2}
        alignItems="center"
        justifyContent="space-between"
        wrap
      >
        <BumpScoreButton
          score={counts.positive - counts.negative}
          onPress={() => bumpDetailsRef.current?.show(bumps)}
        />

        {!readonly && (
          <Row columnGap={2} alignItems="center">
            <IconBumpDown
              size={32}
              disabled={!canBump || myBumpValue == -1}
              onPress={() => {
                // Clicking the button makes that the modal lose focus and close before opening
                authAction(navigation, () => handleClick('down'))
              }}
              mode="text"
            />
            <IconBumpUp
              size={32}
              disabled={!canBump || myBumpValue == 1}
              onPress={() => {
                // Clicking the button makes that the modal lose focus and close before opening
                authAction(navigation, () => handleClick('up'))
              }}
              mode="text"
            />
          </Row>
        )}
      </Row>
    </>
  )
}

export function BumpButtonsReadOnly(props: BumpButtonsProps) {
  const bumpDetailsRef = useRef<BumpDetailsRef>(null)
  const { t } = useTranslation('bumps')
  const [bumps, setBumps] = useState<ContributionBump[]>([])
  const { contributionService, tick } = useReplayApi()
  const refresh = useCallback(() => {
    const bumps = contributionService.getContributionBumps(
      props.contribution?.id || ''
    )
    setBumps(bumps)
  }, [contributionService])

  useEffect(() => {
    refresh()
  }, [tick])

  const counts = useMemo(() => countBumps(bumps), [bumps])
  return (
    <Row columnGap={2}>
      <BumpDetailsModal ref={bumpDetailsRef} />

      <BumpScoreButton
        score={counts.positive - counts.negative}
        onPress={() => bumpDetailsRef.current?.show(bumps)}
      />
    </Row>
  )
}
